import React from "react";
import { format, parseISO } from "date-fns";
import { BANK_RECONCILIATION_REPORT_LABELS } from "@common/constants/reports/bank-reconciliation-report";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { currencyFormatter } from "@common/helpers/currency";
import { ReportCollapsibleSection } from "@components/accounting/ReportCollapsibleSection";
import {
    BankReconciliationReportGroup,
    BankReconciliationReportTransaction,
} from "./types";
import styles from "./BankReconciliationReport.module.scss";

interface TransactionRowsProps {
    transactions: BankReconciliationReportTransaction[];
    total: number;
}

const TransactionRows: React.FC<TransactionRowsProps> = ({
    transactions,
    total,
}) => (
    <div className={styles.transactionBlock}>
        <div className={styles.transactionHeaderRow}>
            <span className={styles.dateCell}>
                {BANK_RECONCILIATION_REPORT_LABELS.dateColumn}
            </span>
            <span className={styles.counterpartyCell}>
                {BANK_RECONCILIATION_REPORT_LABELS.counterpartyColumn}
            </span>
            <span className={styles.descCell}>
                {BANK_RECONCILIATION_REPORT_LABELS.descriptionColumn}
            </span>
            <span className={styles.amountCell}>
                {BANK_RECONCILIATION_REPORT_LABELS.amountColumn}
            </span>
        </div>
        {transactions.map((transaction) => (
            <div key={transaction.id} className={styles.transactionRow}>
                <span className={styles.dateCell}>
                    {format(parseISO(transaction.date), fullDateFullYearFormat)}
                </span>
                <span className={styles.counterpartyCell}>
                    {transaction.counterparty ?? ""}
                </span>
                <span className={styles.descCell}>
                    {transaction.description}
                </span>
                <span className={styles.amountCell}>
                    {currencyFormatter.format(transaction.amount)}
                </span>
            </div>
        ))}
        <div className={styles.transactionSubtotalRow}>
            <span className={styles.dateCell}>
                {BANK_RECONCILIATION_REPORT_LABELS.subtotal}
            </span>
            <span className={styles.counterpartyCell} />
            <span className={styles.descCell} />
            <span className={styles.amountCell}>
                {currencyFormatter.format(total)}
            </span>
        </div>
    </div>
);

interface SubTableProps {
    title: string;
    transactions: BankReconciliationReportTransaction[];
    total: number;
    defaultOpen?: boolean;
}

const SubTable: React.FC<SubTableProps> = ({
    title,
    transactions,
    total,
    defaultOpen,
}) => {
    if (transactions.length === 0) {
        return null;
    }

    return (
        <ReportCollapsibleSection
            title={title}
            count={transactions.length}
            trailingValue={currencyFormatter.format(total)}
            defaultOpen={defaultOpen}
            indentLevel="subsection"
        >
            <TransactionRows transactions={transactions} total={total} />
        </ReportCollapsibleSection>
    );
};

interface BankReconciliationReportSectionProps {
    title: string;
    group: BankReconciliationReportGroup;
    depositsTitle: string;
    paymentsTitle: string;
    emptyMessage: string;
    defaultOpen?: boolean;
}

export const BankReconciliationReportSection: React.FC<
    BankReconciliationReportSectionProps
> = ({
    title,
    group,
    depositsTitle,
    paymentsTitle,
    emptyMessage,
    defaultOpen,
}) => {
    const count = group.deposits.length + group.payments.length;
    const isEmpty = count === 0;

    return (
        <ReportCollapsibleSection
            title={title}
            count={count}
            defaultOpen={defaultOpen}
        >
            {isEmpty ? (
                <p className={styles.emptyMessage}>{emptyMessage}</p>
            ) : (
                <>
                    <SubTable
                        title={depositsTitle}
                        transactions={group.deposits}
                        total={group.depositsTotal}
                        defaultOpen
                    />
                    <SubTable
                        title={paymentsTitle}
                        transactions={group.payments}
                        total={group.paymentsTotal}
                    />
                </>
            )}
        </ReportCollapsibleSection>
    );
};
